import { Box } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { Skeletonize, SkeletonProvider, Tooltip } from '@ui/components';
import { makeStyles } from '@ui/styles';
import React from 'react';
import { usePaymentProviderContext } from '../../Session';
import { IconButton } from '../common/IconButton';
import { ActionButton } from './ActionButton';
import { Chip } from './Chip';
import { LicenseModel } from './createLicenseModel';
import { dummyLicense } from './dummyData';
import { LicenseHeader } from './LicenseHeader';
import {
  AlternatingTableRow,
  HeaderCell,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from './Table';

export interface LicenseLayoutProps {
  license: LicenseModel;
}

const useStyles = makeStyles((theme) => ({
  minusButton: {
    display: 'inline-block',
    backgroundColor: theme.palette.common.white,
  },
}));

export const LicenseLayout: React.FC<LicenseLayoutProps> = ({ license }) => {
  const classes = useStyles();
  const { paymentProvider } = usePaymentProviderContext();

  return (
    <SkeletonProvider enabled={license.id === dummyLicense.id}>
      <TableContainer>
        <Table aria-label='license table'>
          <TableHead>
            <TableRow>
              <TableCell colSpan={6} padding='none'>
                <LicenseHeader id={license.id} licenseKey={license.key} />
              </TableCell>
            </TableRow>

            <TableRow>
              <HeaderCell>License Type</HeaderCell>
              <HeaderCell align='center'>Total Seats</HeaderCell>
              <HeaderCell colSpan={2} />
              <HeaderCell>License Status</HeaderCell>
              <HeaderCell />
            </TableRow>

            <TableRow>
              <Skeletonize as={TableCell} variant='body' SkeletonProps={{ width: 130, height: 18 }}>
                {license.licenseType}
              </Skeletonize>

              <Skeletonize
                as={TableCell}
                variant='body'
                align='center'
                SkeletonProps={{ width: 130, height: 18 }}
              >
                {license.seats}
              </Skeletonize>

              <TableCell colSpan={2} />

              <Skeletonize as={TableCell} colSpan={2} SkeletonProps={{ width: 140, height: 18 }}>
                <Chip status={license.status} />
              </Skeletonize>
            </TableRow>

            <TableRow>
              <HeaderCell>Subscription Number</HeaderCell>
              <HeaderCell align='center'>Seats</HeaderCell>
              <HeaderCell>Renewal Date</HeaderCell>
              <HeaderCell>End Date</HeaderCell>
              <HeaderCell>Subscription Status</HeaderCell>
              <HeaderCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {license.subscriptions.map((row) => {
              const isResellerSubscription = row.invoiceOwnerAccountId !== paymentProvider?.id;

              return (
                <AlternatingTableRow key={row.id}>
                  <Skeletonize as={TableCell} SkeletonProps={{ width: 146, height: 18 }}>
                    {row.subscriptionNumber}
                  </Skeletonize>

                  <Skeletonize
                    as={TableCell}
                    align='center'
                    SkeletonProps={{ width: 146, height: 18 }}
                  >
                    <ResellerTooltip
                      enable={isResellerSubscription}
                      className={classes.minusButton}
                      aria-label='reduce seat count'
                    >
                      <IconButton
                        color='primary'
                        variant='outlined'
                        disabled={isResellerSubscription}
                        icon={<Remove />}
                      />
                    </ResellerTooltip>
                    <Box component='span' display='inline-block' width={56}>
                      {row.seats}
                    </Box>
                    <ResellerTooltip enable={isResellerSubscription}>
                      <IconButton
                        aria-label='increase seat count'
                        color='primary'
                        icon={<Add />}
                        variant='primary'
                        disabled={isResellerSubscription}
                      />
                    </ResellerTooltip>
                  </Skeletonize>

                  <Skeletonize as={TableCell} SkeletonProps={{ width: 146, height: 18 }}>
                    {row.renewalDate}
                  </Skeletonize>

                  <Skeletonize as={TableCell} SkeletonProps={{ width: 146, height: 18 }}>
                    {row.termEndDate}
                  </Skeletonize>

                  <Skeletonize as={TableCell} SkeletonProps={{ width: 146, height: 18 }}>
                    <Chip status={row.status} />
                  </Skeletonize>

                  <Skeletonize
                    as={TableCell}
                    align='center'
                    SkeletonProps={{ width: 146, height: 18 }}
                  >
                    {row.status === 'active' && <ActionButton type='warning'>Cancel</ActionButton>}
                    {row.status === 'canceled' && (
                      <ActionButton type='success'>Resume</ActionButton>
                    )}
                  </Skeletonize>
                </AlternatingTableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow>
              <Skeletonize as={TableCell} colSpan={6} SkeletonProps={{ width: 208, height: 18 }}>
                <ActionButton type='secondary'>Add subscription to existing key</ActionButton>
              </Skeletonize>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </SkeletonProvider>
  );
};

type ResellerTooltipProps = React.HTMLProps<HTMLSpanElement> & { enable: boolean };
const ResellerTooltip: React.FC<ResellerTooltipProps> = ({ enable, ...rest }) =>
  enable ? (
    <Tooltip title='This subscription cannot be edited due to reseller terms. Please contact your reseller to change your seat count.'>
      <span {...rest} />
    </Tooltip>
  ) : (
    <span {...rest} />
  );
